// Featured collection (home): desktop arrows for the scroll-snap track, and the
// colorway hover swap on each product-block-home card.
if (!customElements.get('featured-collection-home')) {
  class FeaturedCollectionHome extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-fch-track]');
      this.prev = this.querySelector('[data-fch-prev]');
      this.next = this.querySelector('[data-fch-next]');
      if (!this.track || !this.prev || !this.next) return;

      this.onScroll = () => {
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.update());
      };
      this.prev.addEventListener('click', () => this.step(-1));
      this.next.addEventListener('click', () => this.step(1));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.resizeObserver = new ResizeObserver(this.onScroll);
      this.resizeObserver.observe(this.track);
      this.update();
    }

    disconnectedCallback() {
      if (this.track) this.track.removeEventListener('scroll', this.onScroll);
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    // Scrolls by one page of visible cards; scroll-snap lines it up.
    step(direction) {
      const item = this.track.querySelector('.fch__item');
      if (!item) return;
      const style = getComputedStyle(this.track);
      const gap = parseFloat(style.columnGap) || 0;
      const inner = this.track.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
      const itemWidth = item.getBoundingClientRect().width + gap;
      const perPage = Math.max(1, Math.floor((inner + gap) / itemWidth));
      this.track.scrollBy({ left: direction * perPage * itemWidth });
    }

    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.prev.disabled = this.track.scrollLeft <= 1;
      this.next.disabled = this.track.scrollLeft >= max - 1;
    }
  }

  customElements.define('featured-collection-home', FeaturedCollectionHome);
}

// Each swatch is its own product (unique SKU). Hovering or focusing one shows that
// product on the card, so clicking the image or title goes there too. Leaving the
// card puts the original product back. Touch taps just follow the swatch link.
if (!customElements.get('product-block-home')) {
  class ProductBlockHome extends HTMLElement {
    connectedCallback() {
      this.swatches = [...this.querySelectorAll('[data-pbh-swatch]')];
      if (this.swatches.length < 2) return;

      this.primary = this.querySelector('.pbh__img--primary');
      this.secondary = this.querySelector('.pbh__img--secondary');
      this.links = this.querySelectorAll('[data-pbh-link]');
      this.title = this.querySelector('[data-pbh-title]');
      this.price = this.querySelector('[data-pbh-price]');
      this.compare = this.querySelector('[data-pbh-compare]');
      this.soldout = this.querySelector('[data-pbh-soldout]');
      this.defaultSwatch = this.swatches.find((s) => s.hasAttribute('data-default')) || this.swatches[0];
      this.current = this.defaultSwatch;

      this.swatches.forEach((swatch) => {
        swatch.addEventListener('pointerenter', (event) => {
          if (event.pointerType === 'mouse') this.select(swatch);
        });
        swatch.addEventListener('focus', () => this.select(swatch));
      });

      this.addEventListener('pointerenter', () => this.preload(), { once: true });
      this.addEventListener('pointerleave', (event) => {
        if (event.pointerType === 'mouse') this.select(this.defaultSwatch);
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.select(this.defaultSwatch);
      });
    }

    // Warms the cache with each colorway's main image so the swap is instant.
    preload() {
      if (!this.primary) return;
      this.swatches.forEach((swatch) => {
        if (!swatch.dataset.srcset) return;
        const img = new Image();
        img.sizes = this.primary.sizes;
        img.srcset = swatch.dataset.srcset;
      });
    }

    select(swatch) {
      if (swatch === this.current) return;
      this.current = swatch;
      const data = swatch.dataset;

      this.swatches.forEach((s) => {
        const active = s === swatch;
        s.classList.toggle('is-active', active);
        if (active) s.setAttribute('aria-current', 'true');
        else s.removeAttribute('aria-current');
      });

      if (this.primary && data.image) {
        this.primary.srcset = data.srcset;
        this.primary.src = data.image;
        this.primary.alt = data.title;
      }

      if (this.secondary) {
        if (data.hoverImage) {
          this.secondary.srcset = data.hoverSrcset;
          this.secondary.src = data.hoverImage;
          this.secondary.hidden = false;
        } else {
          this.secondary.hidden = true;
        }
      }

      this.links.forEach((link) => {
        link.href = data.url;
      });
      if (this.title) this.title.textContent = data.title;
      if (this.price) this.price.textContent = data.price;
      if (this.compare) {
        this.compare.textContent = data.comparePrice;
        this.compare.hidden = !data.comparePrice;
      }
      if (this.soldout) this.soldout.hidden = data.available === 'true';
    }
  }

  customElements.define('product-block-home', ProductBlockHome);
}
